export type LegacySmsTemplateImage = {
  id: string
  name: string
  previewUrl: string
}

export type LegacySmsTemplate = {
  id: string
  category: string
  name: string
  content: string
  calling: string
  type: 'SMS' | 'LMS' | 'MMS'
  images?: LegacySmsTemplateImage[]
}

export type LegacySmsRecipient = {
  id: string
  customerNo: string
  relation: string
  name: string
  phone: string
  baby: string
  scheduleDate?: string
  scheduleTime?: string
}

// This module intentionally contains only non-identifying, in-memory fixture data.
// No recipient, sender, or delivery action is connected to a server or SMS/PUSH provider.
export const legacySmsOffices = ['Mock 지점 A', 'Mock 지점 B']
export const legacySmsCallingNumbers = ['010-0000-0001 (Mock 발신 A)', '010-0000-0002 (Mock 발신 B)']

const mockSmsTemplateImage = (id: string, label: string, color: string): LegacySmsTemplateImage => ({
  id,
  name: `${label}.jpg`,
  previewUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect width="300" height="300" fill="${color}"/><rect x="18" y="18" width="264" height="264" rx="10" fill="none" stroke="#ffffff" stroke-width="3"/><text x="150" y="144" text-anchor="middle" fill="#ffffff" font-size="24" font-family="sans-serif">비식별 Mock</text><text x="150" y="178" text-anchor="middle" fill="#ffffff" font-size="19" font-family="sans-serif">${label}</text></svg>`)}`,
})

export const legacySmsTemplates: LegacySmsTemplate[] = [
  { id: 'mock-sms-template-01', category: '기본', name: 'Mock 기본 안내', content: '안녕하세요. 비식별 Mock 안내 메시지입니다.', calling: legacySmsCallingNumbers[0], type: 'SMS' },
  { id: 'mock-sms-template-02', category: '기본', name: 'Mock 확인 요청', content: 'Mock 요청 사항을 확인해 주세요.', calling: legacySmsCallingNumbers[1], type: 'SMS' },
  {
    id: 'mock-sms-template-03',
    category: '예약',
    name: 'Mock 예약 MMS 안내',
    content: '#{엄마}님, #{촬일} Mock 예약 관련 안내입니다.',
    calling: legacySmsCallingNumbers[0],
    type: 'MMS',
    images: [
      mockSmsTemplateImage('mock-template-image-01', '템플릿 이미지 1', '#678fff'),
      mockSmsTemplateImage('mock-template-image-02', '템플릿 이미지 2', '#c97298'),
      mockSmsTemplateImage('mock-template-image-03', '템플릿 이미지 3', '#01c2d1'),
    ],
  },
  { id: 'mock-sms-template-04', category: '예약', name: 'Mock 일정 확인', content: 'Mock 일정 확인 안내입니다.', calling: legacySmsCallingNumbers[1], type: 'SMS' },
  { id: 'mock-sms-template-05', category: '후속', name: 'Mock 후속 안내', content: 'Mock 후속 절차 안내입니다.', calling: legacySmsCallingNumbers[0], type: 'SMS' },
]

export const legacySmsRecipientSamples: Record<'customer' | 'shoot', LegacySmsRecipient[]> = {
  customer: [
    { id: 'mock-sms-recipient-customer-01', customerNo: 'MOCK-0001', relation: '수신자', name: 'Mock 수신자 A', phone: '010-0000-0101', baby: 'Mock 대상 A' },
    { id: 'mock-sms-recipient-customer-02', customerNo: 'MOCK-0002', relation: '수신자', name: 'Mock 수신자 B', phone: '010-0000-0102', baby: 'Mock 대상 B' },
  ],
  shoot: [
    { id: 'mock-sms-recipient-shoot-01', customerNo: 'MOCK-0101', relation: '수신자', name: 'Mock 촬영 수신자', phone: '010-0000-0201', baby: 'Mock 촬영 대상', scheduleDate: '2026-08-20', scheduleTime: '10:00 ~ 11:00' },
  ],
}

export const legacySmsCodes = [
  { label: '고객번호 #{고번}', code: '#{고번}' },
  { label: '엄마이름 #{엄마}', code: '#{엄마}' },
  { label: '엄마휴대폰 #{엄마폰}', code: '#{엄마폰}' },
  { label: '아빠이름 #{아빠}', code: '#{아빠}' },
  { label: '아빠휴대폰 #{아빠폰}', code: '#{아빠폰}' },
  { label: '아기이름 #{아기}', code: '#{아기}' },
  { label: 'OOPSID #{OOPSID}', code: '#{OOPSID}' },
  { label: '오늘날짜 #{오늘}', code: '#{오늘}' },
  { label: '스튜디오 #{스튜명칭}', code: '#{스튜명칭}' },
  { label: '촬영일 #{촬일}', code: '#{촬일}' },
  { label: '촬영시작시각 #{촬시}', code: '#{촬시}' },
  { label: '촬영종료시각 #{촬시2}', code: '#{촬시2}' },
  { label: '유입경로 #{유입경로}', code: '#{유입경로}' },
  { label: '산부인과 #{산부인과}', code: '#{산부인과}' },
  { label: '조리원 #{조리원}', code: '#{조리원}' },
  { label: '추가경로1 #{추가경로1}', code: '#{추가경로1}' },
  { label: '추가경로2 #{추가경로2}', code: '#{추가경로2}' },
]

export type LegacySmsResult = { id: string; office: string; ad: string; receiverPhone: string; receiver: string; sender: string; calling: string; reserve: string; requestedAt: string; reservedAt: string; sentAt: string; type: string; status: string; result: string; content: string; canCancel: boolean }

export const legacySmsResults: LegacySmsResult[] = Array.from({ length: 30 }, (_, index) => ({
  id: `mock-sms-result-${String(index + 1).padStart(2, '0')}`,
  office: index % 2 ? 'Mock 지점 B' : 'Mock 지점 A', ad: index % 5 === 0 ? '광고' : '', receiverPhone: `010-0000-${String(1000 + index).slice(-4)}`,
  receiver: `Mock 수신자 ${String(index + 1).padStart(2, '0')}`, sender: 'Mock 담당자', calling: '010-0000-0001', reserve: index % 3 === 0 ? '예약' : '즉시',
  requestedAt: '2026-08-20 09:00', reservedAt: index % 3 === 0 ? '2026-08-21 09:00' : '', sentAt: '2026-08-20 09:01', type: index % 4 === 0 ? 'LMS' : 'SMS',
  status: index % 7 === 0 ? '발송 전' : '발송 완료', result: 'Mock 처리 완료', content: `비식별 Mock 발송 내용 ${index + 1}`, canCancel: index % 7 === 0,
}))

export const legacySmsAutoConditions = [{ target: 'Mock 수신 대상', date: 'Mock 조건일', condition: 'Mock 자동 발송 조건' }]
export const legacySmsAutoTargets = [{ id: 'mock-auto-01', round: '1차', time: '2026-08-20 09:00', status: '발송 대기', result: '-', phone: '010-0000-0401', receiver: 'Mock 수신자 A', customerNo: 'MOCK-0401', oldNo: '-', relation: 'Mock 수신자', content: '비식별 Mock 자동 발송 내용입니다.' }]

export type LegacyPushResult = { id:string; customerNo:string; oldNo:string; receiver:string; phone:string; author:string; office:string; status:string; result:string; registeredAt:string; sentAt:string; receivedAt:string; openedAt:string; title:string; content:string; canCancel:boolean }
export const legacyPushResults: LegacyPushResult[] = Array.from({ length: 30 }, (_, index) => ({
  id: `mock-push-${String(index + 1).padStart(2, '0')}`,
  customerNo: `MOCK-P-${String(index + 1).padStart(4, '0')}`,
  oldNo: '-', receiver: `Mock 수신자 ${String(index + 1).padStart(2, '0')}`, phone: `010-0000-${String(2000 + index).slice(-4)}`,
  author: 'Mock 등록자', office: index % 2 ? 'Mock 지점 B' : 'Mock 지점 A', status: index % 5 === 0 ? '대기' : index % 5 === 1 ? '전송중' : index % 5 === 2 ? '완료' : index % 5 === 3 ? '발송오류' : '대기',
  result: index % 5 === 3 ? 'Mock 오류' : 'Mock 처리', registeredAt: '2026-08-20 09:00', sentAt: '2026-08-20 09:01', receivedAt: '2026-08-20 09:02', openedAt: '2026-08-20 09:03',
  title: 'Mock PUSH 안내', content: `비식별 Mock PUSH 내용 ${index + 1}`, canCancel: index % 5 === 0,
}))

// M-04 keeps the grouped Chosen-select interaction while using only generic office labels.
export const legacyPushResultOfficeGroups = [
  { label: 'Mock 권역 01', options: ['Mock 지점 01', 'Mock 지점 02', 'Mock 지점 03', 'Mock 지점 04', 'Mock 지점 05', 'Mock 지점 06', 'Mock 지점 07'] },
  { label: 'Mock 권역 02', options: ['Mock 지점 08'] },
  { label: 'Mock 권역 03', options: ['Mock 지점 09', 'Mock 지점 10', 'Mock 지점 11', 'Mock 지점 12'] },
  { label: 'Mock 권역 04', options: ['Mock 지점 13'] },
  { label: 'Mock 권역 05', options: ['Mock 지점 14'] },
  { label: 'Mock 권역 06', options: ['Mock 지점 15', 'Mock 지점 16'] },
  { label: 'Mock 권역 07', options: ['Mock 지점 17'] },
  { label: 'Mock 권역 08', options: ['Mock 지점 18'] },
  { label: 'Mock 권역 09', options: ['Mock 지점 19'] },
  { label: 'Mock 권역 10', options: ['Mock 지점 20'] },
  { label: 'Mock 권역 11', options: ['Mock 지점 21', 'Mock 지점 22'] },
  { label: 'Mock 권역 12', options: ['Mock 지점 23', 'Mock 지점 24', 'Mock 지점 25'] },
  { label: 'Mock 권역 13', options: [] },
] as const

export const legacyPushResultOffices = legacyPushResultOfficeGroups.flatMap(group => group.options)

export type LegacySmsTemplateRow = {
  id: string
  category: string
  name: string
  content: string
  bytes: number
  type: 'SMS' | 'LMS' | 'MMS'
  calling: string
  rowHeight: number
}

const legacyTemplateRowHeights = [51.984375, 51.984375, 51.984375, 38.1875, 24.390625, 38.1875, 38.1875, 24.390625, 79.578125, 79.578125, 51.984375, 24.390625, 24.390625, 24.390625, 24.390625, 24.390625, 24.390625, 24.390625, 24.390625, 65.78125, 107.171875, 107.171875, 38.1875, 38.1875, 38.1875, 65.78125, 51.984375, 65.78125, 24.390625, 24.390625]

export const legacySmsTemplateRows: LegacySmsTemplateRow[] = legacyTemplateRowHeights.map((rowHeight, index) => ({
  id: `mock-template-row-${String(index + 1).padStart(2, '0')}`,
  category: `Mock 분류 ${String((index % 4) + 1).padStart(2, '0')}`,
  name: `Mock 템플릿 ${String(index + 1).padStart(2, '0')}`,
  content: `비식별 Mock 문자 템플릿 내용 ${index + 1}.`,
  bytes: 24 + index,
  type: index % 9 === 0 ? 'MMS' : index % 3 === 0 ? 'LMS' : 'SMS',
  calling: '010-0000-0001',
  rowHeight,
}))

export type LegacySmsAutoSetting = { id:string; office:string; enabled:boolean; target:string; recipient:string; dateCondition:string; calling:string; condition1:string; condition2:string; condition3:string; advertisement:boolean; content:string }
export const legacySmsAutoSettings: LegacySmsAutoSetting[] = [
  { id:'mock-auto-setting-01', office:'Mock 지점 01', enabled:true, target:'Mock 수신대상 A', recipient:'Mock 수신자 A', dateCondition:'Mock 기준일 - 1일', calling:'010-0000-0001', condition1:'Mock 조건 A', condition2:'-', condition3:'-', advertisement:false, content:'비식별 Mock 자동 발송 안내 A' },
  { id:'mock-auto-setting-02', office:'Mock 지점 02', enabled:false, target:'Mock 수신대상 B', recipient:'Mock 수신자 B', dateCondition:'Mock 기준일 + 1일', calling:'010-0000-0002', condition1:'Mock 조건 B', condition2:'-', condition3:'-', advertisement:true, content:'비식별 Mock 자동 발송 안내 B' },
]

export type LegacySystemSmsPreset = { id: string; category: string; defaultContent: string }
export const legacySystemSmsPresets: LegacySystemSmsPreset[] = [
  { id: 'mock-system-sms-01', category: '촬영스케줄을 등록할 때', defaultContent: '안녕하세요. [Mock 수신자]님\n[Mock 촬영일] [Mock 촬영시각]\n[Mock 스튜디오]' },
  { id: 'mock-system-sms-02', category: '촬영스케줄을 수정할 때', defaultContent: '안녕하세요. [Mock 수신자]님\n변경된 [Mock 촬영일] [Mock 촬영시각]\n[Mock 스튜디오]' },
  { id: 'mock-system-sms-03', category: '계정이 생성될 때', defaultContent: '안녕하세요. [Mock 수신자]님\nMock 앱 계정이 생성되었습니다.\n[Mock 스튜디오]' },
  { id: 'mock-system-sms-04', category: '고객 온라인 결제를 요청할 때', defaultContent: '[Mock 수신자]님\n[Mock 스튜디오] 입니다.\nMock 결제 안내' },
  { id: 'mock-system-sms-05', category: '계정 비밀번호를 초기화할 때', defaultContent: '안녕하세요. [Mock 수신자]님\nMock 비밀번호 초기화 안내\n[Mock 스튜디오]' },
]

export type LegacyCallingNumber = { id: string; office: string; phone: string; name: string }
export const legacyCallingNumbers: LegacyCallingNumber[] = Array.from({ length: 9 }, (_, index) => ({ id: `mock-calling-${index + 1}`, office: `Mock 지점 ${String((index % 3) + 1).padStart(2, '0')}`, phone: `010-0000-${String(index + 1).padStart(4, '0')}`, name: `Mock 발신번호 ${index + 1}` }))
